import styled, { css } from 'styled-components';

const Container = styled.div`
  width: 100%;
  height: auto;
  background-color: #fff;
  overflow-x: hidden;
`;

const Wrap = styled.div`
  display: flex;
  padding: 24px 16px 40px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  background: #fff;
`;

const OptionChip = styled.div<{ $active?: boolean }>`
  padding: 8px 18px;
  border-radius: 999px;
  height: 36px;
  border: 1px solid ${({ $active }) => ($active ? '#272C36' : '#e8e9ed')};
  background-color: ${({ $active }) => ($active ? '#272C36' : '#fff')};
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  cursor: pointer;
`;

const ChipCategory = styled.div<{ $active?: boolean }>`
  color: ${({ $active }) => ($active ? '#fff' : '#787d87')};
  font-size: 13px;
  font-weight: 500;
`;

const SelectResult = styled.div<{ $active?: boolean }>`
  color: ${({ $active }) => ($active ? '#A7ADB8' : '#0c0e12')};
  font-size: 14px;
  font-weight: 500;
`;

const ChipDropdown = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  display: flex;
  gap: 8px;
  z-index: 50;
`;

const SelectedOptionBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedOption = styled.div`
  display: inline-block;
  width: fit-content;
  padding: 2px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #2944b7;
  background-color: #e4edfd;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  white-space: nowrap;
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    background: #f4f5f7;
  }
  thead th:first-child {
    border-top-left-radius: 8px;
  }
  thead th:last-child {
    border-top-right-radius: 8px;
  }
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid #e8e9ed;
  }
`;

const Th = styled.th<{ width?: string; align?: 'left' | 'center' | 'right' }>`
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  width: ${(p) => p.width ?? 'auto'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  vertical-align: top;
`;

const AvgTr = styled.tr`
  background: #f6f9fe;
`;

const TdAvg = styled(Td)`
  font-weight: 700;
  font-size: 15px;
  color: #0c0e12;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0c0e12;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #787d87;
  }
`;

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Img = styled.div<{ $bg?: string }>`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  background-color: ${({ $bg }) => $bg ?? '#fceed4'};
`;

const ButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const ExcelButton = styled.div`
  display: flex;
  width: 36px;
  height: 36px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #e8e9ed;
  background: #fff;
`;

const Button = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e8e9ed;
  color: #424854;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #e9ebee;
  }

  ${(props) =>
    props.disabled &&
    `
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `}
`;

const Select = styled.select`
  height: 36px;
  min-width: 140px;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 14px;
`;

const ToggleGroup = styled.div`
  display: inline-flex;
  flex-direction: row;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  height: 36px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};
  background: ${({ $active }) => ($active ? '#111827' : '#fff')};
  border: none;
  cursor: pointer;

  &:not(:last-child) {
    border-right: 1px solid #e5e7eb;
  }
`;

const ApplyButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #111827;
  background: #111827;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const MoreButton = styled.div`
  width: 100%;
  padding: 8px;
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 30, 38, 0.76);
  border-radius: 6px;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
  }
`;

const TabRow = styled.div`
  display: flex;
  flex-direction: row;
  background: #f4f5f7;
  border-radius: 8px;
  padding: 2px;
  width: max-content;
`;

const TabPill = styled.button<{
  $active?: boolean;
}>`
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #a7adb8;
  font-size: 14px;
  cursor: pointer;
  border-radius: 6px;
  ${({ $active }) =>
    $active &&
    css`
      background: #fff;
      color: #0c0e12;
      font-weight: 500;
      box-shadow:
        0px 4px 16px 0px rgba(39, 44, 54, 0.08),
        0px 1px 2px 0px rgba(39, 44, 54, 0.12),
        0px 2px 6px 0px rgba(39, 44, 54, 0.05);
    `}
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

export const s = {
  Container,
  Wrap,
  OptionChip,
  ChipCategory,
  SelectResult,
  ChipDropdown,
  SelectedOptionBox,
  SelectedOption,
  Table,
  Th,
  Td,
  AvgTr,
  TdAvg,
  Row,
  RowRight,
  Label,
  HeaderRow,
  TitleBox,
  TitleColumn,
  Img,
  ButtonDiv,
  Button,
  Select,
  ToggleGroup,
  ToggleButton,
  ApplyButton,
  MoreButton,
  TabRow,
  TabPill,
  LoaderWrap,
  LoaderImg,
  Header,
  ExcelButton,
  TableWrap,
};
